import React, { useMemo } from 'react';
import { Box, Typography, Chip, Stack, LinearProgress, Tooltip } from '@mui/material';

interface BayInfo {
    bay?: string | null;
    currentFillLevel?: number | null; // expected 0-100
}

interface StorageDetailsProps {
    purpose?: string | null;
    varieties?: string[] | null;
    bays?: BayInfo[] | null;
    inspectionDate?: string | null;
    inspectionId?: number | string | null;
}

const formatDate = (iso?: string | null) => {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso; // fallback
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
        return iso;
    }
};

const StorageDetails: React.FC<StorageDetailsProps> = ({ purpose, varieties, bays, inspectionDate, inspectionId }) => {
    const { averageFill, countedBays, tooltipContent } = useMemo(() => {
        if (!bays || bays.length === 0) {
            return { averageFill: null as number | null, countedBays: 0, tooltipContent: 'No bay data available.' };
        }
        const valid = bays.filter(b => typeof b.currentFillLevel === 'number' && b.currentFillLevel !== null);
        if (valid.length === 0) {
            return { averageFill: null, countedBays: 0, tooltipContent: 'No bay fill levels recorded.' };
        }
        const avg = valid.reduce((sum, b) => sum + (b.currentFillLevel || 0), 0) / valid.length;
        const lines = valid.map(b => `${b.bay || 'Unnamed bay'}: ${b.currentFillLevel}%`);
        const tooltip = `Average of ${valid.length} bay${valid.length === 1 ? '' : 's'}\n` + lines.join('\n');
        return { averageFill: avg, countedBays: valid.length, tooltipContent: tooltip };
    }, [bays]);

    const displayDate = formatDate(inspectionDate);

    return (
        <Box position="relative" sx={{ pb: 3 /* space for bottom-right footer */ }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Storage details
            </Typography>
            <Stack spacing={1}>
                {/* Purpose & Varieties in two columns */}
                <Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ minWidth: 120 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>Purpose</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {purpose ? purpose.charAt(0).toUpperCase() + purpose.slice(1) : '—'}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 160 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>Varieties</Typography>
                            {varieties && varieties.length > 0 ? (
                                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                    {varieties.map(v => (
                                        <Chip key={v} label={v} size="small" />
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary">No varieties</Typography>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Capacity */}
                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>Capacity</Typography>
                    {averageFill !== null ? (
                        <Tooltip title={<Box component="span" sx={{ whiteSpace: 'pre-line' }}>{tooltipContent}</Box>} arrow placement="top">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box sx={{ flex: 1 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={Math.min(100, Math.max(0, averageFill))}
                                        sx={{ height: 6, borderRadius: 1 }}
                                    />
                                </Box>
                                <Typography variant="caption" sx={{ minWidth: 38, textAlign: 'right' }}>{Math.round(averageFill)}%</Typography>
                            </Box>
                        </Tooltip>
                    ) : (
                        <Typography variant="body2">No data</Typography>
                    )}
                </Box>
            </Stack>

            {/* Bottom-right Last inspection */}
            <Box position="absolute" bottom={0} right={0} textAlign="right">
                <Typography variant="caption" color="text.secondary">
                    {displayDate ? displayDate : 'No inspection'}{(inspectionId !== null && inspectionId !== undefined) ? ` · ID: ${inspectionId}` : ''}
                </Typography>
            </Box>
        </Box>
    );
};

export default StorageDetails;
